import { useState } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "~/lib/utils"
import { SectionReveal, StaggerReveal, StaggerItem } from "~/components/company/SectionReveal"

const faqs = [
  {
    question: "What exactly is included in the $8,000-$12,000/month retainer?",
    answer: "You get a dedicated admin contact (~10-15 hrs/week), access to our CTO and COO for strategic decisions, full-stack development capacity (React, Django, FastAPI, Rails), business strategy consulting, workflow mapping and SOP development, and offshore talent hiring and placement. Work is scoped and prioritized collaboratively each month.",
  },
  {
    question: "How do you handle revisions and feedback?",
    answer: "We work iteratively and closely with our clients throughout every engagement. You're in constant knowledge of progress through weekly demos, shared project boards, and regular check-ins. This collaborative approach means fewer surprises and outcomes that match your vision.",
  },
  {
    question: "What if I need more development capacity?",
    answer: "Development capacity scales with what is actually needed. You are never paying for idle time. If a project requires more intensive development, we activate additional developers from our on-call team and adjust accordingly.",
  },
  {
    question: "Do I own the code and IP?",
    answer: "Yes, you own 100% of all code, documentation, SOPs, and intellectual property we create for you from day one. We provide full access to repositories, documentation, and training materials.",
  },
]

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section id="faq" className="w-full py-12 md:py-24 lg:py-32">
      <div className="container px-8 md:px-12 lg:px-16">
        <SectionReveal className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
          <h2 className="text-3xl font-bold leading-tight sm:text-5xl">Frequently Asked Questions</h2>
          <p className="max-w-[700px] text-gray-500 md:text-xl leading-relaxed">
            Everything you need to know before we start building together.
          </p>
        </SectionReveal>

        <div className="max-w-3xl mx-auto">
          <StaggerReveal className="space-y-4" staggerDelay={0.1}>
            {faqs.map((faq, index) => (
              <StaggerItem key={index}>
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden transition-shadow duration-300">
                <button
                  type="button"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  aria-expanded={openIndex === index}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200",
                      openIndex === index && "rotate-180"
                    )}
                  />
                </button>
                {/* kept in DOM for SEO — hidden via max-height, not display:none */}
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-300",
                    openIndex === index ? "max-h-96" : "max-h-0"
                  )}
                  aria-hidden={openIndex !== index}
                >
                  <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
              </StaggerItem>
            ))}
          </StaggerReveal>
        </div>
      </div>
    </section>
  )
}
